import classNames from "classnames/bind";
import React, { useState } from "react";
import { Button } from "../../Button";
import { DropdownMenu } from "../../DropdownMenu";
import { Icon, IconLoading } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import { Tooltip } from "../../Tooltip";
import { colours } from "../../constants/colours";
import styles from "./MetricTile.module.scss";
import type { MetricTileProps } from "./types";

const cx = classNames.bind(styles);

/**
 * A Tile that displays live/static numerical data with optional visualizations
 * and actions.
 */
const MetricTile = ({
  title,
  value,
  description,
  unit,
  emptyText = "-",
  actions,
  action,
  isLoading = false,
  isBusy = false,
  className,
  style,
}: MetricTileProps) => {
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  return (
    <div
      className={cx("container", className)}
      tabIndex={0}
      role="status"
      aria-live="polite"
      style={style}
    >
      <div className={cx("main")}>
        <div>
          <Skeleton
            isLoading={isLoading}
            isBusy={isBusy}
          >
            <p className={cx("title")}>
              <span className={cx("title-wrap")}>{title}</span>
              {description ? (
                <Tooltip
                  title={title}
                  content={description}
                  contentClassName={cx("description-tooltip")}
                  position={["top", "bottom", "left", "right"]}
                >
                  <Icon
                    variant="info"
                    size={16}
                    colour={colours.navy["700"]}
                  />
                </Tooltip>
              ) : null}
            </p>
          </Skeleton>
          <p className={cx("value-and-unit")}>
            {typeof value === "function" ? (
              <Skeleton
                isLoading={isLoading}
                isBusy={isBusy}
                style={{ minWidth: 120 }}
              >
                {value({ className: cx("value"), isLoading, isBusy })}
              </Skeleton>
            ) : (
              <Skeleton
                isLoading={isLoading}
                isBusy={isBusy}
                style={{ minWidth: 120 }}
              >
                <span className={cx("value")}>{value ?? emptyText}</span>
              </Skeleton>
            )}
            {unit ? (
              <Skeleton
                isLoading={isLoading}
                isBusy={isBusy}
                style={{ minWidth: 40 }}
              >
                <span className={cx("unit")}>{unit}</span>
              </Skeleton>
            ) : null}
          </p>
        </div>
        <Skeleton
          isLoading={isLoading}
          isBusy={isBusy}
        >
          <div
            className={cx(
              "actions",
              (isBusy || isActionsMenuOpen) && "always-visible"
            )}
          >
            {isBusy ? (
              <IconLoading speed="fast" />
            ) : actions ? (
              <DropdownMenu
                options={actions}
                rootProps={{ onOpenChange: setIsActionsMenuOpen }}
              >
                <Button
                  variant="outline"
                  icon="dots-three-vertical"
                  size="xx-small"
                />
              </DropdownMenu>
            ) : action ? (
              <Button
                {...action}
                size="xx-small"
              />
            ) : null}
          </div>
        </Skeleton>
      </div>
      <div className={cx("visualization")}>
        {/* IMPROVE: we will add spark lines or some other visualization here in the future when we flesh out our visualization components better. */}
      </div>
    </div>
  );
};

export default MetricTile;
